import React, { useState, useEffect } from 'react';
import { PageLoading } from '../../components/ui/Skeleton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { conferenceService } from '../../services/conferenceService';
import { profileService } from '../../services/profileService';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import HeroSection from './components/HeroSection';
import CallForPapersSection from './components/CallForPapersSection';
import TracksSection from './components/TracksSection';
import ImportantDatesSection from './components/ImportantDatesSection';
import OrganizersSection from './components/OrganizersSection';
import QuickFactsSidebar from './components/QuickFactsSidebar';
import SubmissionModal from './components/SubmissionModal';
import NotificationModal from './components/NotificationModal';
import ProgramSection from './components/ProgramSection';
import Icon from '../../components/AppIcon';
import { scheduleService } from '../../services/scheduleService';


const ConferenceLandingPage = () => {
  const { t } = useTranslation('conferences');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conferenceData, setConferenceData] = useState(null);
  const [tracksData, setTracksData] = useState([]);
  const [organizersData, setOrganizersData] = useState({ chairs: [], organizers: [] });
  const [scheduleData, setScheduleData] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchConferenceData = async () => {
  // Accepts both spellings. The app used ?id= on six routes and
  // ?conferenceId= on three, so links between them only worked if you knew
  // which one each page wanted. Everything now *emits* ?conferenceId=;
  // reading ?id= as well keeps already-shared and bookmarked links alive.
        const conferenceId = searchParams?.get('conferenceId') || searchParams?.get('id');
      
      if (!conferenceId) {
        setError(t('conferenceLanding.noConferenceId'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch conference details
        const conference = await conferenceService?.getById(conferenceId);

        // Fetch tracks
        const tracks = await conferenceService?.getTracks(conferenceId);

        // Fetch organizers
        const orgData = await conferenceService?.getConferenceOrganizers(conferenceId);

        // Resolve avatar URLs for organizers
        const resolveAvatars = async (members) => {
          return Promise.all(members?.map(async (member) => {
            let avatarSignedUrl = null;
            if (member?.avatarUrl) {
              try {
                avatarSignedUrl = await profileService?.getAvatarUrl(member?.avatarUrl);
              } catch (e) { /* ignore */ }
            }
            return { ...member, image: avatarSignedUrl };
          }));
        };

        const chairsWithAvatars = await resolveAvatars(orgData?.chairs || []);
        const organizersWithAvatars = await resolveAvatars(orgData?.organizers || []);

        // Fetch published schedule
        let schedule = [];
        try {
          schedule = await scheduleService?.getPublishedSchedule(conferenceId);
        } catch (e) { /* schedule may not exist yet */ }

        setConferenceData(conference);
        setTracksData(tracks);
        setOrganizersData({ chairs: chairsWithAvatars, organizers: organizersWithAvatars });
        setScheduleData(schedule || []);
      } catch (err) {
        console.error('Error fetching conference data:', err);
        setError(err?.message || 'Failed to load conference data');
      } finally {
        setLoading(false);
      }
    };

    fetchConferenceData();
  }, [searchParams]);

  const callForPapersData = {
    overview: conferenceData?.callForPapers || t('conferenceLanding.callForPapersDefault'),
    topics: [],
    guidelines: [],
    pdfUrl: conferenceData?.callForPapersUrl || null
  };

  const tracksDataTransformed = tracksData?.map((track, index) => ({
    id: track?.id,
    name: track?.name,
    chair: t('conferenceLanding.tba'),
    icon: ['Brain', 'Eye', 'MessageSquare', 'Zap', 'Heart', 'Shield']?.[index % 6],
    description: track?.description || t('conferenceLanding.trackDescriptionDefault'),
    submissionCount: 0,
    acceptanceRate: t('conferenceLanding.tba'),
    keywords: []
  })) || [];

  const importantDatesData = [
    conferenceData?.paperDeadline && {
      title: t('conferenceLanding.dates.fullPaperDeadline'),
      description: t('conferenceLanding.dates.fullPaperDescription'),
      date: conferenceData?.paperDeadline,
      time: "11:59 PM AoE",
      icon: "AlertCircle"
    },
    conferenceData?.reviewDeadline && {
      title: t('conferenceLanding.dates.reviewDeadline'),
      description: t('conferenceLanding.dates.reviewDescription'),
      date: conferenceData?.reviewDeadline,
      time: "11:59 PM AoE",
      icon: "MessageCircle"
    },
    conferenceData?.decisionDate && {
      title: t('conferenceLanding.dates.acceptanceNotification'),
      description: t('conferenceLanding.dates.acceptanceDescription'),
      date: conferenceData?.decisionDate,
      time: "12:00 AM AoE",
      icon: "Mail"
    },
    conferenceData?.cameraReadyDeadline && {
      title: t('conferenceLanding.dates.cameraReadyDeadline'),
      description: t('conferenceLanding.dates.cameraReadyDescription'),
      date: conferenceData?.cameraReadyDeadline,
      time: "11:59 PM AoE",
      icon: "CheckCircle"
    },
    conferenceData?.startDate && {
      title: t('conferenceLanding.dates.conferenceDates'),
      description: t('conferenceLanding.dates.conferenceDatesDescription'),
      date: conferenceData?.startDate,
      time: "9:00 AM Local",
      icon: "Calendar"
    }
  ]?.filter(Boolean);

  const hasOrganizers = (organizersData?.chairs?.length > 0 || organizersData?.organizers?.length > 0);

  const handleSubmitClick = () => {
    if (isAuthenticated) {
      // User is already authenticated, navigate directly to submission portal with conferenceId
      const conferenceId = searchParams?.get('conferenceId') || searchParams?.get('id');
      navigate(`/author-submission-portal?conferenceId=${conferenceId}`);
    } else {
      // User is not authenticated, show the authentication modal
      setIsSubmissionModalOpen(true);
    }
  };

  const handleNotifyClick = () => {
    setIsNotificationModalOpen(true);
  };

  /* Sign in and register are different destinations. `handleLogin` sent people
     to /user-registration — identical to `handleRegister` — so anyone with an
     account who clicked "Sign In to Submit" was asked to create a second one. */
  const handleLogin = () => {
    setIsSubmissionModalOpen(false);
    navigate('/user-sign-in');
  };

  const handleRegister = () => {
    setIsSubmissionModalOpen(false);
    navigate('/user-registration');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <main className="pt-4 flex items-center justify-center min-h-[60vh]">
          <PageLoading label={t('conferenceLanding.loading')} />
        </main>
      </div>
    );
  }

  if (error || !conferenceData) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <main className="pt-4 flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="text-error text-5xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-foreground mb-2">{t('conferenceLanding.errorTitle')}</h2>
            <p className="text-muted-foreground mb-6">{error || t('conferenceLanding.errorDefault')}</p>
            <button
              onClick={() => navigate('/public-conference-board')}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t('conferenceLanding.browseAllConferences')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Transform conference data to match component expectations
  const transformedConferenceData = {
    title: conferenceData?.name,
    type: conferenceData?.conferenceType || 'Academic Conference',
    location: conferenceData?.locationType || 'Hybrid',
    isVirtual: conferenceData?.locationType === 'virtual' || conferenceData?.locationType === 'hybrid',
    startDate: conferenceData?.startDate,
    endDate: conferenceData?.endDate,
    venue: conferenceData?.venueName || conferenceData?.location || 'Venue TBA',
    description: conferenceData?.description || t('conferenceLanding.conferenceDescriptionDefault'),
    submissionDeadline: conferenceData?.paperDeadline,
    notificationDate: conferenceData?.decisionDate,
    submissionFormat: t('conferenceLanding.quickFacts.submissionFormat'),
    fileTypes: t('conferenceLanding.quickFacts.fileTypes'),
    maxFileSize: t('conferenceLanding.quickFacts.maxFileSize'),
    expectedAttendees: t('conferenceLanding.tba'),
    registrationFee: t('conferenceLanding.tba'),
    contactEmail: 'info@conference.org',
    contactPhone: t('conferenceLanding.tba'),
    website: conferenceData?.website || '#',
    bannerImageUrl: conferenceData?.bannerImageUrl || null
  };

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main>
        <HeroSection
          conference={transformedConferenceData}
          onSubmitClick={handleSubmitClick}
          onNotifyClick={handleNotifyClick}
        />
      </main>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <div className="flex-1 min-w-0">
            <CallForPapersSection callForPapers={callForPapersData} />
            {tracksDataTransformed?.length > 0 && <TracksSection tracks={tracksDataTransformed} />}
            {importantDatesData?.length > 0 && <ImportantDatesSection dates={importantDatesData} />}
            {scheduleData?.length > 0 && (
              <ProgramSection
                sessions={scheduleData}
                startDate={conferenceData?.startDate}
                endDate={conferenceData?.endDate}
                tracks={tracksData}
              />
            )}
            {hasOrganizers && <OrganizersSection organizers={organizersData} />}
          </div>

          <aside className="lg:w-80 flex-shrink-0">
            <QuickFactsSidebar
              conference={transformedConferenceData}
              onSubmitClick={handleSubmitClick}
              onNotifyClick={handleNotifyClick}
            />
          </aside>
        </div>
      </div>

      <footer className="bg-muted/30 border-t border-border mt-6 sm:mt-8 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs sm:text-sm text-muted-foreground mb-4">
            &copy; {new Date()?.getFullYear()} {conferenceData?.shortName || conferenceData?.name}. {t('conferenceLanding.footer.allRightsReserved')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs md:text-sm text-muted-foreground">
            <a href="#" className="hover:text-primary transition-250">{t('conferenceLanding.footer.privacyPolicy')}</a>
            <span>•</span>
            <a href="#" className="hover:text-primary transition-250">{t('conferenceLanding.footer.termsOfService')}</a>
            <span>•</span>
            <a href="#" className="hover:text-primary transition-250">{t('conferenceLanding.footer.contact')}</a>
          </div>
        </div>
      </footer>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-10 h-10 sm:w-12 sm:h-12 bg-primary text-primary-foreground shadow-elevation-4 flex items-center justify-center hover:shadow-elevation-5 transition-all duration-250 z-50"
          aria-label={t('conferenceLanding.scrollToTop')}
        >
          <Icon name="ChevronUp" size={20} />
        </button>
      )}

      <SubmissionModal
        isOpen={isSubmissionModalOpen}
        onClose={() => setIsSubmissionModalOpen(false)}
        onLogin={handleLogin}
        onRegister={handleRegister}
      />

      <NotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
      />
    </div>
  );
};

export default ConferenceLandingPage;